// Sprint 35 item 1: the page's mini-game core, with no DOM so a test can play it. Fixed 15 fps steps from step 0 (no tap to start), the
// input of each step recorded as the trace the server replays, and the same shared scoring the server uses (game/minigames/run.js).
import { gameOf } from '../game/minigames/registry.js';
import { noInput, scoreRun } from '../game/minigames/run.js';
import { STEPS_PER_SECOND } from '../game/minigames/balance.js';
import { seedBytes } from '../game/minigames/seed.js';

export const FRAME = { w: 180, h: 300, pad: 80 }; // logical px; shown at 2x. The lower `pad` rows of the bear's frame are its touch pad.
export const FULL_PX = 40; // Bull Run: a thumb this far from the middle is full force
export const STEP_MS = 1000 / STEPS_PER_SECOND;
const BOX = { w: 152, h: 88 }, V = 8, BOX_AT = [14, 108]; // BOX_AT is bear-game-view.js's ANCHORS.layout.box
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Bull Run: the thumb's distance from the middle of the frame sets the lean input, in quarters of full force, + = right.
export const bullInput = dx => clamp(Math.round((dx / FULL_PX) * 4), -4, 4) + 0;
// Keyboard: Left and Right are full force, both together are none.
export const bullKeys = ({ left = false, right = false }) => (left === right ? 0 : right ? 4 : -4);

// The bear: the thumb in the pad sets a target in box pixels (plus 1; 0 means not touching). x runs across the frame, y across the pad.
export function bearTarget(x, y) {
  const py = clamp(y - (FRAME.h - FRAME.pad), 0, FRAME.pad - 1);
  return [Math.round((clamp(x, 0, FRAME.w - 1) * (BOX.w - 1)) / (FRAME.w - 1)) + 1, Math.round((py * (BOX.h - 1)) / (FRAME.pad - 1)) + 1];
}
// A press on the box itself (the box sits at 14,108 in the frame, 1 frame px per box px) aims at that box pixel; anywhere else, null.
export function boxTarget(x, y) {
  const bx = Math.floor(x) - BOX_AT[0], by = Math.floor(y) - BOX_AT[1];
  return bx < 0 || by < 0 || bx >= BOX.w || by >= BOX.h ? null : [bx + 1, by + 1];
}
// Keyboard: a held arrow aims V past where the doorkeeper stands, on that axis; no key held is no touch.
export function bearKeys({ left = false, right = false, up = false, down = false }, me) {
  const kx = (right ? 1 : 0) - (left ? 1 : 0), ky = (down ? 1 : 0) - (up ? 1 : 0);
  if (!kx && !ky) return [0, 0];
  return [clamp(me.x + kx * V, 0, BOX.w - 1) + 1, clamp(me.y + ky * V, 0, BOX.h - 1) + 1];
}

// One run. seed: Uint8Array from seedBytes(miniSeed). start: the page's clock at step 0. The loop is by the clock, not by frames:
// however often tick is called, step n is played when the clock reaches start + (n + 1) * STEP_MS, with the input held at that moment,
// so a whole game ends at start + its length (8.0 s for Bull Run), never sooner.
export function createRunner({ gameId, seed, mode = 'bonus', start = 0 }) {
  const game = gameOf(gameId);
  let state = game.init(seed, { mode }), input = null, steps = 0;
  const trace = [];
  const api = {
    gameId, mode, game,
    get steps() { return steps; },
    get over() { return game.result(state).over; },
    get state() { return state; },
    observe: () => game.observe(state),
    result: () => game.result(state),
    // a value, or a function of the observation (the keyboard in the bear needs to know where the doorkeeper stands)
    setInput(v) { input = v; },
    // Advance to the page clock `now`; returns how many steps ran.
    tick(now) {
      const due = Math.min(game.STEPS, Math.floor(Math.max(0, now - start) / STEP_MS + 1e-9)) - steps;
      let ran = 0;
      for (let i = 0; i < due && !api.over; i++, ran++) {
        const v = typeof input === 'function' ? input(game.observe(state)) : input ?? noInput(gameId);
        trace.push(v);
        state = game.step(state, v);
        steps++;
      }
      return ran;
    },
    // The whole trace, padded with no input to the game's length (what the server wants).
    fullTrace() { const t = trace.slice(); while (t.length < game.STEPS) t.push(noInput(gameId)); return t; },
    // What the shared module scores this run at, for the page to show at once. The server's number is the one that counts.
    score(right, skip = false, sizes = null) { return scoreRun({ gameId, seed, trace: api.fullTrace(), mode, right, skip, sizes }); },
  };
  return api;
}

export const seedOfMessage = msg => seedBytes(msg.seed, msg.steps);
